/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import { DecisionPoint }   from './decision.point';
import { Track         }   from './track';

import {
  MovementDelay,
  DelayType,
  DelaySubType
}   from './movement.delay';

import { MovementHistory } from './movement.history';
import { QuadTreeObject }  from '@/util/quad.tree.object';
import { TrainClass, TrainDirection } from './train.class';
import { TrainType }       from './train';
import { linesIntersect }  from '@/util/util';
import { StopConsist } from '@/models/scenario/stop.consist';

/**
 *
 * @class Movement
 *
 * @brief a single simulation output data point for a single generated train.  Each one of these
 * movements for a generated train forms its travel plot, or MovementHistory.  In the viewer,
 * this "string line" is taken together with the other string lines for other trains, and the
 * whole thing is the string line viewer :)
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class Movement implements QuadTreeObject {

  /** @var _parent the train movement history this move belongs to */
  public    _parent: MovementHistory|null = null;

  /** @var _id the unique id for this movement */
  public        _id: number               = -1;

  /** @var _dp the decision point (physical location) */
  public        _dp: DecisionPoint|null   = null;

  /** @var _track the arriving track */
  public     _track: Track|null           = null;

  /** @var _arrival seconds when the train arrived */
  public   _arrival: number               = 0;

  /** @var _departure seconds when the train left */
  public _departure: number               = 0;

  /** @var _delays any delays while arriving here */
  public     _delays: MovementDelay[]     = [];

  /** @var _totalDelay sum of all delay for this movement */
  public _totalDelay: number              = 0;

  /** the movement position in the history array for a given train */
  public _nth: number                     = 0;

  /* support for ordered traversal */

  /** the previous movement */
  public _previous: Movement|null         = null;

  /** the next movement */
  public _next: Movement|null             = null;

  /** support for train consist change tracking */
  public _consist: StopConsist|null       = null;

  /* support for QuadTreeObject */

  /*
   * the bounding box for movement is x1,y1 being the last move depart, and x2,y2
   * being this move depart.  The bounding box will be set by the plotter when it
   * plots the movements.  (see ScenarioLayer.render() )
   *
   */

  public      _qtX: number                = 0;
  public      _qtY: number                = 0;
  public  _qtWidth: number                = 0;
  public _qtHeight: number                = 0;

  /** @brief fetch the unique id for this object (normally a Movement) */
  objId(): number {

    return this._id;
  }

  /** @brief test if the internal segments of this movement, intersect with the given line */
  intersects(px: number, py: number, qx: number, qy: number): boolean {

    /* get the actual path of this movement */

    const lines: [number,number,number,number][] = this.lines();
    let     hit: boolean                         = false;

    /* walk the path, looking for an intersection ... */

    lines.forEach((seg: [number,number,number,number]) => {

      if (linesIntersect(px, py, qx, qy, ...seg)) {

        hit = true;
      }

    });

    /* pass back the results */

    return hit;
  }

  /** @brief fetch the lines (in world space) that make up this movement */
  lines(): any[] {

    /*
     * figure out this move and the previous one; we need the previous move because we
     * have to draw a line from the previous departure point to this arrival point.
     *
     */

    let mv: Movement       = this;
    let previous: Movement = mv;
    const     lines: any[] = [];

    if ((mv._nth > 0) && mv._parent) {

      previous = mv._parent._history[mv._nth - 1];
    }

    /*
     * build the set of lines for this movement:
     *
     *   - first line is from the upper bound (previous departure point) to our arrival point
     *
     *   - second line is from our arrival point to our departure point (if there is a time
     *     difference).
     *
     *
     */

    if (!mv._parent) {

      /* we don't have a movement history yet */

      return [];
    }

    /* the line from previous departure point to our arrival point */

    {

      const x1: number = previous._departure;
      const y1: number = previous._dp ? previous._dp._mileage : 0;
      const x2: number = mv._arrival;
      const y2: number = mv._dp ? mv._dp._mileage : 0;

      /*
       * in the case where this is the very first movement, it will just be a point
       * instead of a line.
       *
       */

      lines.push([x1, y1, x2, y2]);

    }

    /* the line from arrival to departure at the current mileage */

    if (this._arrival !== this._departure) {

      const x1: number = mv._arrival;
      const y1: number = mv._dp ? mv._dp._mileage : 0;
      const x2: number = mv._departure;
      const y2: number = mv._dp ? mv._dp._mileage : 0;

      lines.push([x1, y1, x2, y2]);

    }

    return lines;
  }

  /** is this an "elbow" line? */
  hasMidpoint(): boolean {

    return this._arrival !== this._departure;
  }

  /** if there were delays, then there are two lines segments, and we have an elbow, or a mid-point */
  midpoint(): [number, number] {

    return [this._arrival, this._dp ? this._dp._mileage: 0];
  }

  /** @brief fetch the objects x position */
  x(): number {
    return this._qtX;
  }

  /** @brief fetch the objects y position */
  y(): number {
    return this._qtY;
  }

  /* @brief fetch the objects width */
  width(): number {
    return this._qtWidth;
  }

  /** @brief fetch the objects height */
  height(): number {
    return this._qtHeight;
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - */

  /** @brief standard constructor */
  constructor(   parent: MovementHistory|null,
                     id: number,
                     dp: DecisionPoint|null,
                  track: Track|null,
                arrival: number,
              departure: number) {

    this._parent    = parent;
    this._id        = id;
    this._dp        = dp;
    this._track     = track;
    this._arrival   = arrival;
    this._departure = departure;
  }

  /** @brief add a delay to a train move */
  add(delay: MovementDelay) {

    this._totalDelay         += delay._hours;

    if (this._parent) {

      this._parent._totalDelay += delay._hours;
    }

    this._delays.push(delay);
  }

  /** @brief fetch a summary of the delays associated with this movement */
  delayReport(): Map<DelayType, Map<DelaySubType, number>> {

    const report: Map<DelayType, Map<DelaySubType, number>> = new Map<DelayType, Map<DelaySubType, number>>();

    for (const delay of this._delays) {

      const dur: number     = delay._hours;
      const   t: DelayType    = MovementDelay.toDelayType(delay._type);
      const   s: DelaySubType = MovementDelay.toDelaySubType(delay._type);

      if (!report.has(t)) {

        report.set(t, new Map<DelaySubType, number>());
      }

      const totals: Map<DelaySubType, number>|null = report.get(t) ?? null;

      if (totals) {

        if (!totals.has(s)) {

          totals.set(s, 0.0);
        }

        const current: number = totals.get(s) ?? 0;

        totals.set(s, current + dur);
      }
    }

    return report;
  }

  quickTitle(): string {

    let dir: string = '';

    if (!this._parent || !this._parent._train || !this._parent._train._tc || !this._track) {

      return '';
    }

    switch (this._parent._train._tc._dir) {

      case TrainDirection.EAST: {
        dir = 'EAST';
        break;
      }

      case TrainDirection.WEST: {
        dir = 'WEST';
        break;
      }
    }

    const trainName = this._parent._train._name;

    let type = '';
    switch (this._parent._train._type) {

      case TrainType.ACTUAL: {
        type = 'A';
        break;
      }

      case TrainType.EVEN: {
        type = 'E';
        break;
      }

      case TrainType.EXPONENTIAL: {
        type = 'X';
        break;
      }

      case TrainType.SCHEDULED: {
        type = 'S';
        break;
      }
    }

    return '[' + Track.typeToString(this._track._type) + this._track._track + '] ' + trainName + ' (' + type + ') ' + dir;
  }

  trainClass(): TrainClass {

    if (this._consist) {

      return this._consist._tc;
    }

    /* fall through to the train's default */

    return this._parent._train._tc;
  }

  /** fetch the effective train length */
  length(): number {

    if (this._consist) {

      return this._consist._length;
    }

    /* fall through to the train's default */

    return this._parent._train._length;
  }

  /** fetch the effective train tonnage */
  weight(): number {

    if (this._consist) {

      return this._consist._tonnage;
    }

    /* fall through to the train's default */

    return this._parent._train._tonnage;
  }

  /** fetch the effective train priority */
  priority(): number {

    if (this._consist) {

      return this._consist._priority;
    }

    /* fall through to the train's default */

    return this._parent._train._priority;
  }

  /** fetch the effective train overtake delay */
  overtake(): number {

    if (this._consist) {

      return this._consist._overtake;
    }

    /* fall through to the train's default */

    return this._parent._train._overtake;
  }
}
